import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ScheduleDialog from './ScheduleDialog';
import { Schedule, CreateScheduleRequest } from '../../models/Schedule';
import * as scheduleService from '../../services/scheduleService';

interface SchedulesTabProps {
  zoneId?: number;
  zoneName?: string;
  hotelId?: number;
  isEditing: boolean;
  afterSave?: () => void;
}

const SchedulesTab: React.FC<SchedulesTabProps> = ({ zoneId, zoneName, hotelId, isEditing, afterSave }) => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; schedule: Schedule | null }>({ open: false, schedule: null });

  useEffect(() => {
    if (isEditing && zoneId) {
      loadSchedules();
    }
  }, [isEditing, zoneId]);

  const loadSchedules = async () => {
    if (!zoneId) return;
    try {
      setLoading(true);
      setError(null);
      const schedulesData = await scheduleService.getSchedulesForZone(zoneId);
      setSchedules(schedulesData);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError('Zamanlama yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setFormMode('edit');
    setScheduleDialogOpen(true);
  };

  const handleDeleteSchedule = async (schedule: Schedule) => {
    try {
      await scheduleService.deleteSchedule(schedule.id);
      setSchedules((prev) => prev.filter((s) => s.id !== schedule.id));
      setDeleteConfirm({ open: false, schedule: null });
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Zamanlama silinemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleCreateSchedule = () => {
    setSelectedSchedule(null);
    setFormMode('create');
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async (scheduleData: CreateScheduleRequest) => {
    try {
      if (formMode === 'create') {
        const newSchedule = await scheduleService.createSchedule({
          ...scheduleData,
          zoneId: zoneId!,
        });
        setSchedules((prev) => [...prev, newSchedule]);
      } else if (formMode === 'edit' && selectedSchedule) {
        await scheduleService.updateSchedule(selectedSchedule.id, scheduleData);
        await loadSchedules();
      }
      setScheduleDialogOpen(false);
      if (afterSave) afterSave();
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError('Zamanlama kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  if (!isEditing) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CalendarTodayRoundedIcon sx={{ fontSize: 32, color: '#ccc', mb: 1 }} />
        <Typography sx={{ fontSize: '0.9rem', color: '#888' }}>
          Zamanlama eklemek için önce zone'u kaydedin.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e' }}>
          Zamanlamalar
        </Typography>
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={handleCreateSchedule}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
            borderRadius: '10px', px: 2, py: 0.7,
            backgroundColor: '#4294e3', color: '#fff',
            '&:hover': { backgroundColor: '#3680c9' }
          }}
        >
          Zamanlama Ekle
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2, borderRadius: '10px',
            backgroundColor: 'rgba(229, 57, 53, 0.05)',
            border: '1px solid rgba(229, 57, 53, 0.15)',
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: '#4294e3' }} />
        </Box>
      ) : schedules.length === 0 ? (
        /* Empty State */
        <Box sx={{
          py: 4, textAlign: 'center',
          borderRadius: '14px', border: '2px dashed #e8e8e8',
          backgroundColor: '#fafafa'
        }}>
          <CalendarTodayRoundedIcon sx={{ fontSize: 36, color: '#ccc', mb: 1.5 }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#888', mb: 0.5 }}>
            Zamanlama Yok
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#aaa', mb: 2.5 }}>
            Bu zone varsayılan ayarlarla çalışıyor.
          </Typography>
          <Button
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={handleCreateSchedule}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
              borderRadius: '10px', px: 3, py: 0.8,
              backgroundColor: '#4294e3', color: '#fff',
              '&:hover': { backgroundColor: '#3680c9' }
            }}
          >
            İlk Zamanlamayı Oluştur
          </Button>
        </Box>
      ) : (
        /* Schedule List */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {schedules.map((schedule) => (
            <Box
              key={schedule.id}
              sx={{
                p: 2, borderRadius: '14px',
                border: schedule.isCurrent ? '2px solid rgba(66, 148, 227, 0.4)' : '1px solid #f0f0f0',
                backgroundColor: schedule.isCurrent ? 'rgba(66, 148, 227, 0.03)' : '#fff',
                opacity: schedule.isActive ? 1 : 0.6,
                transition: 'all 0.2s',
                '&:hover': { backgroundColor: schedule.isCurrent ? 'rgba(66, 148, 227, 0.06)' : '#fafafa' }
              }}
            >
              {/* Top Row: Name + Badges + Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}>
                    {schedule.name}
                  </Typography>
                  {schedule.priority === 0 && (
                    <Chip
                      icon={<PriorityHighRoundedIcon sx={{ fontSize: 12 }} />}
                      label="Yüksek"
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.6rem', fontWeight: 600,
                        backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#e65100',
                        border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '6px',
                        '& .MuiChip-icon': { color: '#e65100' }
                      }}
                    />
                  )}
                  {schedule.isCurrent && (
                    <Chip
                      label="Aktif"
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.6rem', fontWeight: 600,
                        backgroundColor: 'rgba(66, 148, 227, 0.1)', color: '#4294e3',
                        borderRadius: '6px'
                      }}
                    />
                  )}
                  {!schedule.isActive && (
                    <Chip
                      label="Pasif"
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.6rem', fontWeight: 600,
                        backgroundColor: '#f5f5f5', color: '#999',
                        borderRadius: '6px'
                      }}
                    />
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title={schedule.isActive ? 'Aktif' : 'Pasif'}>
                    <Switch
                      size="small"
                      checked={schedule.isActive}
                      onChange={async () => {
                        try {
                          await scheduleService.updateSchedule(schedule.id, {
                            isActive: !schedule.isActive,
                          });
                          await loadSchedules();
                        } catch (err) {
                          console.error('Error toggling schedule status:', err);
                          setError('Durum güncellenemedi.');
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#4294e3' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4294e3' },
                      }}
                    />
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleEditSchedule(schedule)} sx={{ color: '#888', '&:hover': { color: '#4294e3' } }}>
                    <EditRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm({ open: true, schedule })}
                    sx={{ color: '#bbb', '&:hover': { color: '#c62828' } }}
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Info Row: Days, Time, Volume, Shuffle, Priority */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <CalendarTodayRoundedIcon sx={{ fontSize: 13, color: '#aaa' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {schedule.formattedDays || scheduleService.formatScheduleDays(schedule.daysOfWeek)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <AccessTimeRoundedIcon sx={{ fontSize: 13, color: '#aaa' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {schedule.formattedTimeRange || `${schedule.startTime} - ${schedule.endTime}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <VolumeUpRoundedIcon sx={{ fontSize: 13, color: '#aaa' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {Math.round(schedule.volume * 100)}%
                  </Typography>
                </Box>
                {schedule.shuffle && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <ShuffleRoundedIcon sx={{ fontSize: 13, color: '#aaa' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>Karışık</Typography>
                  </Box>
                )}
                {schedule.priority > 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>
                    Öncelik: {schedule.priority}
                  </Typography>
                )}
              </Box>

              {/* Folders Row */}
              {schedule.musicFolders.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, alignItems: 'center' }}>
                  <FolderOpenRoundedIcon sx={{ fontSize: 13, color: '#bbb', mr: 0.3 }} />
                  {schedule.musicFolders.map((folder, idx) => (
                    <Chip
                      key={idx}
                      label={folder}
                      size="small"
                      sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 500,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(66, 148, 227, 0.06)',
                        color: '#4294e3',
                        border: '1px solid rgba(66, 148, 227, 0.1)',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, schedule: null })}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(229, 57, 53, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Zamanlamayı Sil
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            <strong style={{ color: '#1a1a2e' }}>"{deleteConfirm.schedule?.name}"</strong> zamanlamasını silmek istiyor musunuz?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setDeleteConfirm({ open: false, schedule: null })}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={() => deleteConfirm.schedule && handleDeleteSchedule(deleteConfirm.schedule)}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#c62828', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#b71c1c' }
              }}
            >
              Sil
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Schedule Form Dialog */}
      {scheduleDialogOpen && (
        <ScheduleDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          onSave={handleSaveSchedule}
          zoneId={zoneId}
          zoneName={zoneName}
          schedule={selectedSchedule}
          mode={formMode}
        />
      )}
    </Box>
  );
};

export default SchedulesTab;
